import PropTypes from 'prop-types';
import { useState } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { Announcement, Breadcrumbs, Button, Checkbox, SelectableCard } from '@utilities';
import { createAskWhy, createQuestion, FOLDER_ID_CUSTOM } from '@api/instant_survey';
import { updateUserCreditsRemaining } from '@redux/slices/user';

import SubscriptionOverview from '../../components/SubscriptionOverview';

import Cart from './components/Cart';
import Column from '../../layouts/Column';
import ModalConfirmAskWhy from '../../components/ModalConfirmAskWhy';

import { payWithCard, payWithCredits, payWithCompletes } from './utilities/constants';
import { getCheckoutPageComponent } from './utilities/helpers';

import styles from './_index.module.scss';

const Checkout = () => {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [usePromoCredits, setUsePromoCredits] = useState(false);

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const userData = useSelector((state) => state?.user?.data);
  const canAccessAskWhy = userData?.permissions?.canAccessAskWhy;
  const userSurveyCreditsRemaining = userData?.surveyInfo?.userCredits;

  const hasPromoCredits = userData?.surveyInfo?.hasPromoCredits || false;
  const hasSubscription = userData?.permissions?.canAccessSurveySubscription;

  const cartItems = location.state?.cartItems || [];
  const jobId = location.state?.survey?.report.id;

  const isCreditsDisabled =
    userSurveyCreditsRemaining < cartItems.reduce((acc, item) => acc + item.prices.credits, 0);

  const paymentOptions = [
    ...(hasSubscription
      ? [
          {
            ...payWithCompletes,
            description: <SubscriptionOverview />,
          },
        ]
      : []),
    ...(canAccessAskWhy?.canPayWithCredits
      ? [
          {
            ...payWithCredits,
            description: `${payWithCredits.description} ${userSurveyCreditsRemaining || 0}`,
            helperText: isCreditsDisabled
              ? 'Please reach out to your Numerator team for more credits'
              : '',
            isDisabled: isCreditsDisabled,
          },
        ]
      : []),
    ...(canAccessAskWhy?.canPayWithCard ? [payWithCard] : []),
  ];
  const [selectedPaymentOption, setSelectedPaymentOption] = useState(
    paymentOptions.find((paymentOption) => !paymentOption.isDisabled)
  );

  const handleSubmit = async () => {
    const controller = new AbortController();
    const signal = controller.signal;

    setIsSubmitting(true);
    setSubmitError(null);
    const queryParams = `jobId=${jobId}&paymentMethod=${selectedPaymentOption.key}`;
    try {
      const createdQuestions = await Promise.all(
        location.state.survey.questions.map((question) =>
          createQuestion({
            question: {
              folder: FOLDER_ID_CUSTOM,
              text: question.text,
              type: question.type,
              ...(question.answers && { answers: question.answers }),
            },
            signal,
          })
        )
      );

      const response = await createAskWhy({
        cancelUrl: `${window.location.origin}/dashboard/checkout/cancel?${queryParams}`,
        paymentMethod: selectedPaymentOption.key,
        questions: createdQuestions,
        signal,
        successUrl: `${window.location.origin}/dashboard/checkout/success?${queryParams}`,
        use_promo_credits: usePromoCredits,
        ...location.state.survey,
      });

      switch (selectedPaymentOption.key) {
        case payWithCard.key:
          if (response.stripeCheckoutUrl) {
            window.location.href = response.stripeCheckoutUrl;
          } else {
            console.error('No stripe checkout url provided', response);
          }
          break;
        case payWithCredits.key:
          dispatch(
            updateUserCreditsRemaining({ userCreditsRemaining: response.userCreditsRemaining })
          );
          return navigate(`/dashboard/checkout/success?${queryParams}`, {
            replace: true,
          });
        case payWithCompletes.key:
          return navigate(`/dashboard/checkout/success?${queryParams}`, {
            replace: true,
          });
        default:
          break;
      }
    } catch (error) {
      if (!signal.aborted) {
        setSubmitError(error);
        setIsSubmitting(false);
      }
    }
  };

  const baseTotal = cartItems.reduce((acc, item) => acc + (Number(item?.prices?.credits) || 0), 0);

  const usingCredits = selectedPaymentOption?.key === 'credits';

  const promoCredits =
    usingCredits && usePromoCredits && !isNaN(Number(userSurveyCreditsRemaining))
      ? Number(userSurveyCreditsRemaining)
      : 0;

  const finalTotal = Math.max(baseTotal - promoCredits, 0);

  const canLaunch = !usingCredits || finalTotal <= userSurveyCreditsRemaining;

  return (
    <Column>
      <Breadcrumbs />
      <h1>Payment</h1>
      <div className={styles['checkout']}>
        <div className={styles['checkout-column']}>
          <h2>Method of Payment</h2>
          {!paymentOptions.length && (
            <Announcement
              header="No payment option available"
              text="Please reach out to your Numerator team to setup payment options."
              variant="warn"
            />
          )}
          {paymentOptions.length > 0 && (
            <ul className={styles['checkout-payment-options']}>
              {paymentOptions.map((paymentOption) => (
                <li key={paymentOption.key}>
                  <SelectableCard
                    data-log={`Checkout_MethodOfPayment_${paymentOption.key}`}
                    data-testid="payment-option"
                    header={paymentOption.name}
                    helperText={paymentOption.helperText}
                    id={`payment-option-${paymentOption.key}`}
                    isActive={paymentOption.key === selectedPaymentOption?.key}
                    isDisabled={paymentOption.isDisabled}
                    onClick={() => setSelectedPaymentOption(paymentOption)}
                    text={paymentOption.description}
                  />
                </li>
              ))}
            </ul>
          )}

          <div className={styles['checkout-additional-options']}>
            {hasPromoCredits && (
              <div className={styles['checkout-promo-credits']}>
                <Checkbox
                  data-testid="use-promo-credits"
                  id="use-promo-credits"
                  isChecked={usePromoCredits}
                  label="Use promotional credits"
                  onChange={() => setUsePromoCredits(!usePromoCredits)}
                  value={usePromoCredits}
                />
              </div>
            )}
          </div>
        </div>

        {paymentOptions.length > 0 && (
          <div className={styles['checkout-column']}>
            <h2>Purchase Details</h2>
            <Cart items={cartItems} paymentOption={selectedPaymentOption || paymentOptions[0]} />
            <div className={styles['checkout-actions']}>
              <Button
                data-log="Checkout_BackToReport"
                id="checkout-cancel"
                onClick={() => navigate(-1)}
                text="Back to Report"
                variant="secondary"
              />
              <Button
                data-log={`Checkout_Continue_${selectedPaymentOption?.key}`}
                id="checkout-continue"
                isDisabled={!selectedPaymentOption || cartItems.length === 0 || !canLaunch}
                onClick={() => setIsConfirmationOpen(true)}
                text="Continue"
              />
            </div>
          </div>
        )}
      </div>

      {isConfirmationOpen && (
        <ModalConfirmAskWhy
          isSubmitting={isSubmitting}
          onClose={() => setIsConfirmationOpen(false)}
          onSubmit={handleSubmit}
          panelistCount={100}
          paymentKey={selectedPaymentOption.key}
          submitError={submitError}
          submitText="Checkout"
        />
      )}
    </Column>
  );
};

const CheckoutRoutes = ({ route }) => {
  return (
    <Routes>
      <Route element={<Checkout />} index />
      {route.routes?.map(({ id, path }) => (
        <Route element={getCheckoutPageComponent({ path })} key={id} path={path} />
      ))}
    </Routes>
  );
};

CheckoutRoutes.propTypes = {
  route: PropTypes.object.isRequired,
};

export default CheckoutRoutes;
